import { useState, useEffect, useRef } from 'react';
import { createUserWithEmailAndPassword, updateProfile, OAuthProvider, signInWithPopup } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions, db } from '../../lib/firebase';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import AppBar from '../../components/AppBar';
import GlobalTabs from '../../components/GlobalTabs';
import { GlobalInput } from '../../components/GlobalInput';
import { GlobalButton } from '../../components/GlobalButton';
import { GlobalMicroButton } from '../../components/GlobalMicroButton';
import styles from './page.module.css';
import { Toast } from '../../components/Toast';
import ContentGrid from '../../components/ContentGrid';
import AppFooter from '../../components/AppFooter';
import { useLocale } from '../../context/DynamicLocaleProvider';
import { AVAILABLE_LANGUAGES, LANGUAGE_TO_FLAG_MAP, LANGUAGE_NAMES } from '../../utils/languages';
import { getAuthErrorMessage } from '../../utils/authErrors';

export default function SignupPage() {
  const { t, locale } = useLocale();
  const selectRef = useRef<HTMLSelectElement | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isMicrosoftLoading, setIsMicrosoftLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [isInvitationSignup, setIsInvitationSignup] = useState(false);
  const [invitationData, setInvitationData] = useState<Record<string, unknown> | null>(null);
  const [companyName, setCompanyName] = useState<string>('');
  const [, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const invitationId = searchParams.get('invitation');
  const internalSignupToken = process.env.REACT_APP_INTERNAL_SIGNUP_TOKEN;
  const urlToken = searchParams.get('token');
  const isInternalSignup =
    !!internalSignupToken && !!urlToken && urlToken === internalSignupToken;

  const handleLanguagePick = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    try {
      localStorage.setItem('language', value);
    } catch {}
    // Reload to ensure provider re-reads localStorage override immediately
    window.location.reload();
  };

  const openNativeLanguagePicker = () => {
    const sel = selectRef.current;
    if (!sel) return;
    try {
      sel.focus();
      sel.click();
    } catch {}
  };

  // Load invitation data if invitation ID is present (read-only for display purposes)
  // This platform is invitation-only, so signup requires an invitation token
  useEffect(() => {
    const loadInvitationData = async () => {
      // If no invitation token, show error immediately (invitation-only platform)
      if (!invitationId) {
        // Allow internal signups when a valid static token is present
        if (isInternalSignup) {
          setIsInvitationSignup(false);
          setInvitationData(null);
          setCompanyName('');
          return;
        }

        setError(
          t('signup_invitation_required') ||
            'Signup requires a valid invitation link. Please use the invitation link sent to your email.'
        );
        setShowToast(true);
        return;
      }
      
      try {
        setLoading(true);
        
        // Read invitation document for display purposes only (no write/delete operations)
        const userRef = doc(db, 'users', invitationId);
        const userSnap = await getDoc(userRef);
        
        if (userSnap.exists()) {
          const userData = userSnap.data();
          setEmail(userData.email || '');
          setInvitationData(userData);
          setIsInvitationSignup(true);
          
          // Fetch company name if company ID exists
          const companyId = userData.company || userData.companyId;
          if (companyId) {
            try {
              const companyRef = doc(db, 'companies', companyId);
              const companySnap = await getDoc(companyRef);
              if (companySnap.exists()) {
                const companyData = companySnap.data();
                setCompanyName(companyData.name || companyId);
              } else {
                setCompanyName(companyId);
              }
            } catch (companyError) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Error fetching company:', companyError);
      }
              setCompanyName(companyId || '');
            }
          }
        } else {
          setError(t('signup_invalid_invitation_link'));
          setShowToast(true);
        }
      } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('❌ Error loading invitation data:', error);
      }
        let errorMessage = t('signup_failed_load_invitation_data') || 'Failed to load invitation data';
        
        // Provide more specific error messages; never show raw Firebase/Firestore messages
        if (error instanceof Error) {
          if (error.message.includes('Missing or insufficient permissions')) {
            errorMessage = t('signup_invitation_permission_denied') || 'Permission denied. Please contact support if this issue persists.';
          } else if (error.message.includes('not found')) {
            errorMessage = t('signup_invalid_invitation_link') || 'Invalid invitation link. The invitation may have expired or been used already.';
          } else if (error.message.includes('Firebase') || error.message.includes('auth/') || error.message.includes('firestore')) {
            errorMessage = t('signup_failed_load_invitation_data') || 'Failed to load invitation data.';
          } else {
            errorMessage = `${errorMessage}: ${error.message}`;
          }
        }
        
        setError(errorMessage);
        setShowToast(true);
      } finally {
        setLoading(false);
      }
    };

    loadInvitationData();
  }, [invitationId, isInternalSignup, t]);

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    // Invitation-only platform: require invitation token
    if (!invitationId && !isInternalSignup) {
      setError(
        t('signup_invitation_required') ||
          'Signup requires a valid invitation link. Please use the invitation link sent to your email.'
      );
      setIsLoading(false);
      setShowToast(true);
      return;
    }

    if (password !== confirm) {
      setError(t('signup_passwords_do_not_match'));
      setIsLoading(false);
      return;
    }

    try {
      // Create the user account
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // For invitation-based signups, finalize the migration of the temporary user document.
      if (!isInternalSignup) {
        // Update Firebase user profile with name if available from invitation data
        if (invitationData?.firstName && invitationData?.lastName) {
          await updateProfile(user, {
            displayName: `${invitationData.firstName} ${invitationData.lastName}`
          });
        }

        // Call Cloud Function to finalize signup (migrate document, delete old, send notifications)
        // This is required for all signups on this invitation-only platform
        const finalizeUserSignup = httpsCallable<
          { oldUserId: string },
          { success: boolean; message: string; newUid: string; oldUserId: string }
        >(functions, 'finalizeUserSignup');

        const result = await finalizeUserSignup({
          oldUserId: invitationId as string
        });

        if (!result.data.success) {
          throw new Error('Failed to finalize user signup');
        }

      }

      setShowToast(true);
      // Redirect to home after successful signup
      setTimeout(() => {
        navigate('/home');
      }, 2000);
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('❌ Signup error:', err);
      }
      const message = getAuthErrorMessage(err, t, 'signup');
      setError(message ?? t('signup_unexpected_error'));
      setShowToast(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMicrosoftSignup = async () => {
    setIsMicrosoftLoading(true);
    setError(null);

    try {
      const provider = new OAuthProvider('microsoft.com');
      provider.addScope('email');
      provider.addScope('profile');

      await signInWithPopup(auth, provider);

      // If signup was via invitation link, migrate invitation user doc to new auth UID
      if (invitationId) {
        const finalizeUserSignup = httpsCallable<
          { oldUserId: string },
          { success: boolean; message: string; newUid: string; oldUserId: string }
        >(functions, 'finalizeUserSignup');
        await finalizeUserSignup({ oldUserId: invitationId });
      }

      setShowToast(true);
      setIsMicrosoftLoading(false);
    } catch (err) {
      setIsMicrosoftLoading(false);
      const message = getAuthErrorMessage(err, t, 'signup');
      if (message == null) return; // Popup closed, don't show error
      setError(message);
      setShowToast(true);
    }
  };

  return (
    <div>

      {/* App bar */}
      <AppBar />

      {/* Content */}
      <ContentGrid rows="auto 548px auto 48px">

        {/* Spacer */}
        <div style={{ gridRow: 1 }} />

        {/* Form container */}
        <div className={styles.formContainer}>

          {/* Language switcher */}
          <GlobalMicroButton
            type="button"
            onClick={openNativeLanguagePicker}
            className={styles.languageButton}
            icon={
              <img
                src={`https://cdn.ipregistry.co/flags/emojitwo/${LANGUAGE_TO_FLAG_MAP[locale] || 'de'}.svg`}
                alt="Language Icon"
                width={16}
                height={16}
              />
            }
          >
            {LANGUAGE_NAMES[locale] || (locale?.toUpperCase?.() || 'DE')}
          </GlobalMicroButton>

          {/* Hidden native select to open system dropdown */}
          <select
            ref={selectRef}
            aria-label="Select language"
            defaultValue={locale}
            onChange={handleLanguagePick}
            className={styles.languageSelectOverlay}
          >
            {AVAILABLE_LANGUAGES.map(lang => (
              <option key={lang.code} value={lang.code}>
                {lang.name}
              </option>
            ))}
          </select>

          {/* Headline & paragraph */}
          <h1 className={styles.title}>
            {isInvitationSignup ? t('signup_complete_account_setup') : t('signup_create_account')}
          </h1>
          <p className={styles.subtitle}>
            {isInvitationSignup 
              ? t('signup_set_password_description')
              : t('signup_get_started_description')
            }
          </p>

          {/* Form / Tabs wrapper */}
          <div className={styles.formWrapper}>

            {/* Show info message if no invitation token and not internal token-based signup */}
            {!invitationId && !isInternalSignup && (
              <div className={styles.invitationInfoBox}>
                <p className={styles.invitationInfoText}>
                  {t('signup_invitation_required') || 'A valid invitation link is required to sign up.'}
                </p>
              </div>
            )}

            {/* Email and Company info - displayed above tabs */}
            {isInvitationSignup && (
              <div style={{ marginBottom: '8px' }}>
                {email && (
                  <div className={styles.infoField}>
                    <p className={styles.infoText}>
                      <span className={styles.infoLabel}>{t('signup_email_label') || 'Email'}:</span><b>&nbsp;{email}</b>
                    </p>
                  </div>
                )}
                {companyName && (
                  <div className={styles.infoField}>
                    <p className={styles.infoText}>
                      <span className={styles.infoLabel}>{t('signup_company_label') || 'Company'}:</span><b>&nbsp;{companyName}</b> 
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Tabs: E-Mail (form + encryption) and SSO (Microsoft) */}
            <GlobalTabs
              tabs={[
                {
                  label: t('signup_tab_email') || 'E-Mail',
                  content: (
                    <form onSubmit={handleSignup} className={styles.form}>
                      {/* Email field for non-invitation (e.g. internal token-based) signups */}
                      {!isInvitationSignup && (
                        <GlobalInput
                          label={t('signup_email_label')}
                          type="email"
                          value={email}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                          required
                        />
                      )}

                      {/* Password */}
                      <GlobalInput
                        label={t('signup_password_label')}
                        type="password"
                        value={password}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                        required
                      />

                      {/* Confirm Password */}
                      <GlobalInput
                        label={t('signup_confirm_password_label')}
                        type="password"
                        value={confirm}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirm(e.target.value)}
                        required
                      />

                      {/* Button */}
                      <div className={styles.buttonContainer}>
                        <GlobalButton
                          type="submit"
                          isLoading={isLoading}
                          disabled={isLoading || (!invitationId && !isInternalSignup)}
                        >
                          {t('signup_button')}
                        </GlobalButton>

                        <div className={styles.securityBadge}>
                          <img src="/assets/icons/shield.lefthalf.filled.green.svg" alt="Shield" height={12} width={12} />
                          <span>{t('signup_encrypted_badge')}</span>
                        </div>
                      </div>

                      {/* Login alternative */}
                      <p className={styles.signupText}>
                        {t('signup_already_have_account')}{' '}
                        <Link to="/login" className={styles.link}>
                          {t('signup_login_link')}
                        </Link>
                      </p>
                    </form>
                  ),
                },
                {
                  label: t('signup_tab_sso') || 'SSO',
                  content: (
                    <div className={styles.ssoTabContent}>
                      <GlobalButton
                        type="button"
                        onClick={handleMicrosoftSignup}
                        isLoading={isMicrosoftLoading}
                        disabled={isLoading || isMicrosoftLoading || (!invitationId && !isInternalSignup)}
                        variant="secondary"
                        className={styles.microsoftButton}
                        icon={
                          <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect x="0.5" y="0.5" width="9.5" height="9.5" fill="#F25022"/>
                            <rect x="10.5" y="0.5" width="9.5" height="9.5" fill="#7FBA00"/>
                            <rect x="0.5" y="10.5" width="9.5" height="9.5" fill="#00A4EF"/>
                            <rect x="10.5" y="10.5" width="9.5" height="9.5" fill="#FFB900"/>
                          </svg>
                        }
                      >
                        {isMicrosoftLoading ? (t('signup_microsoft_loading') || 'Signing up...') : (t('signup_microsoft_button') || 'Continue with Microsoft')}
                      </GlobalButton>

                      <p className={styles.signupText}>
                        {t('signup_already_have_account')}{' '}
                        <Link to="/login" className={styles.link}>
                          {t('signup_login_link')}
                        </Link>
                      </p>
                    </div>
                  ),
                },
              ]}
            />
          </div>

          {/* Toast */}
          <Toast
            message={error ? error : t('signup_success_message')}
            type={error ? 'error' : 'success'}
            isVisible={showToast}
            onClose={() => setShowToast(false)}
          />
        </div>

        {/* Spacer */}
        <div style={{ gridRow: 3 }} />

        {/* Footer */}
        <AppFooter inline />
      </ContentGrid>
    </div>
  );
}